import {
  Box,
  CircularProgress,
  List,
  ListSubheader,
  styled,
  Typography,
} from '@mui/material';
import { makeStyles } from '@mui/styles';
import React from 'react';
import { applyOffsetToTimestampMs } from '../../../helpers/utilityFunctions/utilityFunctions';
import { Alert, AlertMoment } from '../../../model/passive/alertMoment';
import { PassiveParameterType } from '../../../model/passive/passiveParameterType';
import MomentCard from '../../Cards/MomentCard';

const useStyles = makeStyles((theme: any) => ({
  listSubHeaderRoot: {
    lineHeight: '24px !important',
    backgroundColor: '#E5E5E5 !important',
    borderRadius: '5px !important',
    color: '#252525 !important',
  },
}));

// The alert's day in the patient's timezone, in the same format as the date on
// its card (e.g. "Sat, 14 Mar 2026").
const AlertDay = (alert: Alert) =>
  new Date(applyOffsetToTimestampMs(alert.dateTime, alert.utcTimeZone))
    .toUTCString()
    .split(' ')
    .slice(0, 4)
    .join(' ');

const CentredBox = styled(Box)({
  display: 'flex',
  flexDirection: 'column',
  height: '100vh',
  width: '385px',
  justifyContent: 'center',
  alignItems: 'center',
});

interface AlertListProps {
  alertType: PassiveParameterType;
  selectedAlertData: AlertMoment[];
  selectedMomentId: number;
  isLoading: boolean;
  onClick: (event: React.MouseEvent<HTMLDivElement>, id: number) => void;
}

/** A list component for showing path insight alerts. */
export const AlertList: React.FC<AlertListProps> = ({
  alertType,
  selectedAlertData,
  selectedMomentId,
  isLoading,
  onClick,
}) => {
  const classes = useStyles();

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        border: '1px solid',
        borderColor: 'black',
        borderRadius: '10px',
        pt: 1,
        pl: 2,
        pr: 2,
        rowGap: '10px',
        //small hack to position the list. This will need to change as more content gets added to to this component
        maxHeight: 'calc(100vh - 180px)',
        overflow: 'auto',
      }}
    >
      <ListSubheader
        component="div"
        disableGutters
        classes={{ root: classes.listSubHeaderRoot }}
      >
        <Typography sx={{ ml: 1 }} variant="h6" component="h6">
          <strong>At Risk {alertType} Moments</strong>
        </Typography>
      </ListSubheader>

      {isLoading ? (
        <CentredBox>
          <CircularProgress size={64} />
        </CentredBox>
      ) : selectedAlertData.length ? (
        // The section headers sent by the API (e.g. "Alerts") are not shown,
        // so all sections are listed together under the title above.
        <List
          sx={{
            display: 'flex',
            flexDirection: 'column',
            rowGap: 1,
          }}
        >
          {selectedAlertData
            .flatMap((section) => section.data ?? [])
            .map((alert: Alert, index, alerts) => (
              <React.Fragment key={alert.id}>
                {/* Label the start of each day */}
                {(index === 0 ||
                  AlertDay(alert) !== AlertDay(alerts[index - 1])) && (
                  <Typography
                    component="li"
                    variant="body2"
                    sx={{
                      color: 'text.secondary',
                      fontWeight: 'bold',
                      mt: index > 0 ? 1 : 0,
                    }}
                  >
                    {AlertDay(alert)}
                  </Typography>
                )}
                <MomentCard
                  index={alert.id}
                  type={alertType}
                  data={alert}
                  selected={selectedMomentId === alert.id}
                  onClick={onClick}
                />
              </React.Fragment>
            ))}
        </List>
      ) : (
        <Typography
          sx={{
            textAlign: 'center',
            mb: '10px',
          }}
          variant="h6"
          component="h6"
        >
          No Alerts to Show
        </Typography>
      )}
    </Box>
  );
};
